/**
 * AgentSettingsTab - Configure AI support agent persona, style, and timing
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User,
  Clock,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Loader2,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Upload,
  Image,
} from 'lucide-react';
import { api } from '../../../services/api';

interface WritingStyle {
  tone: 'formal' | 'friendly' | 'casual';
  conciseness: number;
  use_emojis: boolean;
  personalization: number;
}

interface ResponseTiming {
  urgent_min_minutes: number;
  urgent_max_minutes: number;
  normal_min_hours: number;
  normal_max_hours: number;
  daytime_start: number;
  daytime_end: number;
}

interface AgentName {
  id: string;
  language_code: string;
  names: string[];
  is_active: boolean;
}

const LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  it: 'Italian',
  nl: 'Dutch',
  sv: 'Swedish',
  da: 'Danish',
  no: 'Norwegian',
  fi: 'Finnish',
  pl: 'Polish',
  cs: 'Czech',
  hu: 'Hungarian',
  pt: 'Portuguese',
  zh: 'Chinese',
  ja: 'Japanese',
  ko: 'Korean',
  default: 'Default (Fallback)',
};

export function AgentSettingsTab() {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    identity: true,
    style: true,
    timing: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="space-y-6">
      {/* Agent Identity Section */}
      <CollapsibleSection
        title="Agent Identity"
        icon={User}
        isExpanded={expandedSections.identity}
        onToggle={() => toggleSection('identity')}
      >
        <AgentIdentitySection />
      </CollapsibleSection>

      {/* Writing Style Section */}
      <CollapsibleSection
        title="Writing Style"
        icon={MessageSquare}
        isExpanded={expandedSections.style}
        onToggle={() => toggleSection('style')}
      >
        <WritingStyleSection />
      </CollapsibleSection>

      {/* Response Timing Section */}
      <CollapsibleSection
        title="Response Timing"
        icon={Clock}
        isExpanded={expandedSections.timing}
        onToggle={() => toggleSection('timing')}
      >
        <ResponseTimingSection />
      </CollapsibleSection>
    </div>
  );
}

function CollapsibleSection({
  title,
  icon: Icon,
  isExpanded,
  onToggle,
  children,
}: {
  title: string;
  icon: typeof User;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-primary-600" />
          <span className="font-medium text-gray-900">{title}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {isExpanded && <div className="p-6 border-t border-gray-200">{children}</div>}
    </div>
  );
}

function AgentIdentitySection() {
  const queryClient = useQueryClient();
  const [editingLang, setEditingLang] = useState<string | null>(null);
  const [editingNames, setEditingNames] = useState<string>('');
  const [newLang, setNewLang] = useState('');
  const [newNames, setNewNames] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const { data: namesData, isLoading: namesLoading } = useQuery({
    queryKey: ['ai-agent-names'],
    queryFn: () => api.getAIAgentNames(),
  });

  const { data: avatarsData, isLoading: avatarsLoading } = useQuery({
    queryKey: ['ai-agent-avatars'],
    queryFn: () => api.getAIAgentAvatars(),
  });

  const { data: settingsData } = useQuery({
    queryKey: ['ai-agent-settings'],
    queryFn: () => api.getAIAgentSettings(),
  });

  const updateNamesMutation = useMutation({
    mutationFn: ({ language, names }: { language: string; names: string[] }) =>
      api.updateAIAgentNames(language, { names }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-names'] });
      setEditingLang(null);
    },
  });

  const addNamesMutation = useMutation({
    mutationFn: (data: { language_code: string; names: string[] }) => api.addAIAgentNames(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-names'] });
      setShowAddForm(false);
      setNewLang('');
      setNewNames('');
    },
  });

  const deleteNamesMutation = useMutation({
    mutationFn: (language: string) => api.deleteAIAgentNames(language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-names'] });
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (settings: Record<string, unknown>) => api.updateAIAgentSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-settings'] });
    },
  });

  const handleSaveNames = (language: string) => {
    const names = editingNames
      .split(',')
      .map((n) => n.trim())
      .filter((n) => n);
    if (names.length > 0) {
      updateNamesMutation.mutate({ language, names });
    }
  };

  const handleAddNames = () => {
    const names = newNames
      .split(',')
      .map((n) => n.trim())
      .filter((n) => n);
    if (newLang && names.length > 0) {
      addNamesMutation.mutate({ language_code: newLang, names });
    }
  };

  const handleSelectAvatar = (avatarId: string) => {
    const currentIdentity = settingsData?.settings?.agent_identity || {
      title: 'Support Specialist',
      show_avatar: true,
      default_avatar_id: null,
    };
    updateSettingsMutation.mutate({
      agent_identity: { ...currentIdentity, default_avatar_id: avatarId },
    });
  };

  if (namesLoading || avatarsLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const names = namesData?.names || [];
  const avatars = avatarsData?.avatars || [];
  const identity = settingsData?.settings?.agent_identity;

  return (
    <div className="space-y-8">
      {/* Agent Title */}
      <div>
        <label htmlFor="agent-title" className="block text-sm font-medium text-gray-700 mb-2">Agent Title</label>
        <input
          id="agent-title"
          type="text"
          defaultValue={identity?.title || 'Support Specialist'}
          onBlur={(e) => {
            const currentIdentity = identity || {
              title: 'Support Specialist',
              show_avatar: true,
              default_avatar_id: null,
            };
            updateSettingsMutation.mutate({
              agent_identity: { ...currentIdentity, title: e.target.value },
            });
          }}
          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Support Specialist"
        />
        <p className="text-xs text-gray-500 mt-1">
          Title displayed in support emails (e.g., "Support Specialist", "Customer Care Agent")
        </p>
      </div>

      {/* Avatar Selection */}
      <div>
        <span className="block text-sm font-medium text-gray-700 mb-3">Agent Avatar</span>
        <div className="grid grid-cols-6 gap-4 max-w-lg">
          {avatars.map((avatar) => (
            <button
              key={avatar.id}
              onClick={() => handleSelectAvatar(avatar.id)}
              className={`relative p-2 rounded-lg border-2 transition-all ${
                identity?.default_avatar_id === avatar.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              title={avatar.name}
            >
              {avatar.url.endsWith('.svg') || avatar.url.startsWith('/avatars/') ? (
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <Image className="w-6 h-6 text-gray-400" />
                </div>
              ) : (
                <img
                  src={avatar.url}
                  alt={avatar.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              )}
              {identity?.default_avatar_id === avatar.id && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          ))}
          <button className="p-2 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors">
            <div className="w-12 h-12 flex items-center justify-center">
              <Upload className="w-6 h-6 text-gray-400" />
            </div>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Select a default avatar for the AI agent or upload a custom one
        </p>
      </div>

      {/* Agent Names by Language */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="block text-sm font-medium text-gray-700">Agent Names by Language</span>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Language
          </button>
        </div>

        {showAddForm && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={newLang}
                onChange={(e) => setNewLang(e.target.value)}
                placeholder="Language code (e.g., en)"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <input
                type="text"
                value={newNames}
                onChange={(e) => setNewNames(e.target.value)}
                placeholder="Names (comma-separated)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <button
                onClick={handleAddNames}
                disabled={addNamesMutation.isPending}
                className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {addNamesMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewLang('');
                  setNewNames('');
                }}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                  Language
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                  Agent Names
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {names.map((item: AgentName) => (
                <tr key={item.language_code} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-900">
                      {LANGUAGE_LABELS[item.language_code] || item.language_code}
                    </span>
                    <span className="text-gray-500 text-sm ml-2">({item.language_code})</span>
                  </td>
                  <td className="px-4 py-3">
                    {editingLang === item.language_code ? (
                      <input
                        type="text"
                        value={editingNames}
                        onChange={(e) => setEditingNames(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        autoFocus
                      />
                    ) : (
                      <span className="text-gray-600">{item.names.join(', ')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      {editingLang === item.language_code ? (
                        <>
                          <button
                            onClick={() => handleSaveNames(item.language_code)}
                            disabled={updateNamesMutation.isPending}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                          >
                            {updateNamesMutation.isPending ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => setEditingLang(null)}
                            className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setEditingLang(item.language_code);
                              setEditingNames(item.names.join(', '));
                            }}
                            className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {item.language_code !== 'default' && (
                            <button
                              onClick={() => {
                                if (confirm(`Delete names for ${item.language_code}?`)) {
                                  deleteNamesMutation.mutate(item.language_code);
                                }
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          The AI agent will use a random name from this list based on the customer's detected
          language
        </p>
      </div>
    </div>
  );
}

function WritingStyleSection() {
  const queryClient = useQueryClient();

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['ai-agent-settings'],
    queryFn: () => api.getAIAgentSettings(),
  });

  const updateMutation = useMutation({
    mutationFn: (settings: Record<string, unknown>) => api.updateAIAgentSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-settings'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const style: WritingStyle = settingsData?.settings?.writing_style || {
    tone: 'friendly',
    conciseness: 70,
    use_emojis: false,
    personalization: 80,
  };

  const updateStyle = (updates: Partial<WritingStyle>) => {
    updateMutation.mutate({
      writing_style: { ...style, ...updates },
    });
  };

  return (
    <div className="space-y-6">
      {/* Tone Selector */}
      <div>
        <span className="block text-sm font-medium text-gray-700 mb-2">Tone</span>
        <div className="flex gap-3">
          {(['formal', 'friendly', 'casual'] as const).map((tone) => (
            <button
              key={tone}
              onClick={() => updateStyle({ tone })}
              className={`px-4 py-2 rounded-lg border-2 capitalize transition-all ${
                style.tone === tone
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {tone}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Formal: Professional and polished. Friendly: Warm and approachable. Casual: Relaxed and
          conversational.
        </p>
      </div>

      {/* Conciseness Slider */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="conciseness-slider" className="text-sm font-medium text-gray-700">Conciseness</label>
          <span className="text-sm text-gray-500">{style.conciseness}%</span>
        </div>
        <input
          id="conciseness-slider"
          type="range"
          min="0"
          max="100"
          value={style.conciseness}
          onChange={(e) => updateStyle({ conciseness: parseInt(e.target.value) })}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>More detailed</span>
          <span>More concise</span>
        </div>
      </div>

      {/* Personalization Slider */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="personalization-slider" className="text-sm font-medium text-gray-700">Personalization</label>
          <span className="text-sm text-gray-500">{style.personalization}%</span>
        </div>
        <input
          id="personalization-slider"
          type="range"
          min="0"
          max="100"
          value={style.personalization}
          onChange={(e) => updateStyle({ personalization: parseInt(e.target.value) })}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>Generic responses</span>
          <span>Use customer name & context</span>
        </div>
      </div>

      {/* Emoji Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-medium text-gray-700">Use Emojis</span>
          <p className="text-xs text-gray-500">Include occasional emojis in responses</p>
        </div>
        <button
          onClick={() => updateStyle({ use_emojis: !style.use_emojis })}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            style.use_emojis ? 'bg-primary-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
              style.use_emojis ? 'translate-x-6' : ''
            }`}
          />
        </button>
      </div>

      {updateMutation.isPending && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Saving...
        </div>
      )}
    </div>
  );
}

function ResponseTimingSection() {
  const queryClient = useQueryClient();

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['ai-agent-settings'],
    queryFn: () => api.getAIAgentSettings(),
  });

  const updateMutation = useMutation({
    mutationFn: (settings: Record<string, unknown>) => api.updateAIAgentSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-settings'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const timing: ResponseTiming = settingsData?.settings?.response_timing || {
    urgent_min_minutes: 25,
    urgent_max_minutes: 35,
    normal_min_hours: 2,
    normal_max_hours: 3,
    daytime_start: 8,
    daytime_end: 20,
  };

  const updateTiming = (updates: Partial<ResponseTiming>) => {
    updateMutation.mutate({
      response_timing: { ...timing, ...updates },
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Configure how long the AI waits before sending email responses. Delays make responses feel
        more natural and human-like.
      </p>

      {/* Urgent Tickets */}
      <div className="bg-red-50 p-4 rounded-lg border border-red-100">
        <h4 className="font-medium text-red-800 mb-3">Urgent Tickets</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="urgent-min-delay" className="block text-sm text-red-700 mb-1">Minimum delay</label>
            <div className="flex items-center gap-2">
              <input
                id="urgent-min-delay"
                type="number"
                min="1"
                max="60"
                value={timing.urgent_min_minutes}
                onChange={(e) => updateTiming({ urgent_min_minutes: parseInt(e.target.value) })}
                className="w-20 px-3 py-2 border border-red-200 rounded-lg text-center"
              />
              <span className="text-sm text-red-600">minutes</span>
            </div>
          </div>
          <div>
            <label htmlFor="urgent-max-delay" className="block text-sm text-red-700 mb-1">Maximum delay</label>
            <div className="flex items-center gap-2">
              <input
                id="urgent-max-delay"
                type="number"
                min="1"
                max="60"
                value={timing.urgent_max_minutes}
                onChange={(e) => updateTiming({ urgent_max_minutes: parseInt(e.target.value) })}
                className="w-20 px-3 py-2 border border-red-200 rounded-lg text-center"
              />
              <span className="text-sm text-red-600">minutes</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-red-600 mt-2">
          Urgent tickets use keywords like "urgent", "broken", "can't access", etc.
        </p>
      </div>

      {/* Normal Tickets */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
        <h4 className="font-medium text-blue-800 mb-3">Normal Tickets</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="normal-min-delay" className="block text-sm text-blue-700 mb-1">Minimum delay</label>
            <div className="flex items-center gap-2">
              <input
                id="normal-min-delay"
                type="number"
                min="1"
                max="24"
                value={timing.normal_min_hours}
                onChange={(e) => updateTiming({ normal_min_hours: parseInt(e.target.value) })}
                className="w-20 px-3 py-2 border border-blue-200 rounded-lg text-center"
              />
              <span className="text-sm text-blue-600">hours</span>
            </div>
          </div>
          <div>
            <label htmlFor="normal-max-delay" className="block text-sm text-blue-700 mb-1">Maximum delay</label>
            <div className="flex items-center gap-2">
              <input
                id="normal-max-delay"
                type="number"
                min="1"
                max="24"
                value={timing.normal_max_hours}
                onChange={(e) => updateTiming({ normal_max_hours: parseInt(e.target.value) })}
                className="w-20 px-3 py-2 border border-blue-200 rounded-lg text-center"
              />
              <span className="text-sm text-blue-600">hours</span>
            </div>
          </div>
        </div>
      </div>

      {/* Daytime Hours */}
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
        <h4 className="font-medium text-amber-800 mb-3">Daytime Sending Hours</h4>
        <p className="text-sm text-amber-700 mb-3">
          Emails are only sent during daytime hours in the customer's timezone
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="daytime-start-hour" className="block text-sm text-amber-700 mb-1">Start hour</label>
            <div className="flex items-center gap-2">
              <input
                id="daytime-start-hour"
                type="number"
                min="0"
                max="23"
                value={timing.daytime_start}
                onChange={(e) => updateTiming({ daytime_start: parseInt(e.target.value) })}
                className="w-20 px-3 py-2 border border-amber-200 rounded-lg text-center"
              />
              <span className="text-sm text-amber-600">:00</span>
            </div>
          </div>
          <div>
            <label htmlFor="daytime-end-hour" className="block text-sm text-amber-700 mb-1">End hour</label>
            <div className="flex items-center gap-2">
              <input
                id="daytime-end-hour"
                type="number"
                min="0"
                max="23"
                value={timing.daytime_end}
                onChange={(e) => updateTiming({ daytime_end: parseInt(e.target.value) })}
                className="w-20 px-3 py-2 border border-amber-200 rounded-lg text-center"
              />
              <span className="text-sm text-amber-600">:00</span>
            </div>
          </div>
        </div>
      </div>

      {updateMutation.isPending && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Saving...
        </div>
      )}
    </div>
  );
}
